import { message } from "antd";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { getCharge, putCharge } from "../../../helpers/api";
import _ from "lodash";
import { RequiredIndicator } from "../../../components";
import smallTalk from "smalltalk";
import { CHARGE_VALUE_TYPES } from "../../../helpers/config";
import { getUser } from "../../../helpers/auth";

export default function EditChargeForm({ onSuccess, id }) {
  const { handleSubmit, register, reset, watch, setValue } = useForm();
  const [isLoading, setIsLoading] = useState(false);

  const user = getUser();

  // Fetch charge data
  const { data: charge, isLoading: isFetching } = useQuery({
    queryFn: () => getCharge(user.facility.id, id),
    queryKey: ["charge", id],
    enabled: !!id,
    onSuccess: (data) => {
      if (data) {
        setValue("title", data.title);
        setValue("description", data.description);
        setValue("type", data.type);
        setValue("valueType", data.valueType);
        setValue("value", data.value);
      }
    },
  });

  const { mutateAsync } = useMutation(
    (data) => putCharge(user.facility.id, id, { ...data }),
    {
      onSuccess: (data, variables, context) => {
        if (data.status === 200) {
          message.success("Charge updated successfully");
          onSuccess();
          return;
        }
        throw data;
      },
      onError: (error, variables, context) => {
        const err = error.response?.data?.message;
        if (_.isArray(err)) {
          err.map((err) => message.error(err.message));
        } else {
          message.error(err || "Error updating charge");
        }
      },
      retry: true,
    }
  );

  const postSubmit = (data) => {
    smallTalk
      .confirm(
        "Update Charge",
        "You are about to update this charge. Continue?",
        {
          buttons: {
            ok: "YES",
            cancel: "NO",
          },
        }
      )
      .then(() => mutateAsync(data));
  };

  const valueType = watch("valueType");

  if (isFetching) {
    return <div className="text-center p-4">Loading charge details...</div>;
  }

  if (!charge) {
    return <div className="text-center p-4 text-danger">Charge not found</div>;
  }

  return (
    <form onSubmit={handleSubmit(postSubmit)}>
      <div className="field">
        <label htmlFor="title">
          Title
          <RequiredIndicator />
        </label>
        <input
          type="text"
          id="title"
          autoFocus
          {...register("title", { required: true })}
          className="input w-100"
          placeholder="Enter charge title"
        />
      </div>

      <div className="field">
        <label htmlFor="valueType">
          Value Type
          <RequiredIndicator />
        </label>
        <select
          id="valueType"
          {...register("valueType", { required: true })}
          className="input w-100">
          <option value="">Select value type</option>
          {CHARGE_VALUE_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="value">
          {valueType === "percentage" ? "Percentage (%)" : "Amount"}
          <RequiredIndicator />
        </label>
        <input
          type="number"
          id="value"
          step={valueType === "percentage" ? "0.01" : "0.01"}
          min={0}
          max={valueType === "percentage" ? 100 : undefined}
          {...register("value", {
            required: true,
            min: 0,
            max: valueType === "percentage" ? 100 : undefined,
          })}
          className="input w-100"
          placeholder={
            valueType === "percentage"
              ? "Enter percentage (0-100)"
              : "Enter amount"
          }
        />
        {valueType === "percentage" && (
          <small>Enter a value between 0 and 100</small>
        )}
      </div>

      <div className="field">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          {...register("description")}
          className="textarea w-100"
          rows={4}
          placeholder="Enter charge description"></textarea>
      </div>

      <div className="field mt-4">
        <button
          type="submit"
          className="button app-btn btn-prim w-100 p-2"
          disabled={isLoading}>
          {isLoading ? "Updating..." : "Update Charge"}
        </button>
      </div>
    </form>
  );
}
